import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Heart, Users, Calendar, Clock, Star, Check, ChevronRight, X,
  Sparkles, MessageCircle, Camera, Brain, Coffee, Gift, Mail,
  CreditCard, User, Phone, MapPin
} from "lucide-react";
import { Button } from "../components/ui/button";
import { FooterSection } from "../components/landing/FooterSection";

const projectBenefits = [
  { icon: Camera, title: "이미지 트레이닝", desc: "매력적인 이성이 되는 법" },
  { icon: Brain, title: "멘탈 체인지", desc: "가치높은 이성을 알아보는 안목" },
  { icon: MessageCircle, title: "1:1 연애상담", desc: "월 2회 개별 코칭" },
  { icon: Phone, title: "실시간 카톡상담", desc: "1:1 맞춤 상담" },
  { icon: Coffee, title: "1:1 소개팅 10회", desc: "실질적 경험 쌓기" },
  { icon: Users, title: "전체 연애 코칭", desc: "월 2회 그룹 세션" },
  { icon: Sparkles, title: "외모 업그레이드", desc: "패션, 피부, 헤어, 분위기" },
  { icon: Gift, title: "추가 혜택", desc: "6개월 힐링카페 무료" },
];

const testimonials = [
  {
    name: "김○○",
    age: "52세",
    content: "3개월 만에 정말 연인이 생겼어요. 처음엔 반신반의했는데, 이미지 트레이닝과 멘탈 코칭이 정말 효과적이었습니다.",
  },
  {
    name: "이○○", 
    age: "47세",
    content: "20년 만에 다시 연애를 시작했습니다. 손리즈 선생님의 코칭 덕분에 자신감을 되찾았어요.",
  },
  {
    name: "박○○",
    age: "55세",
    content: "소개팅 경험이 없어서 걱정했는데, 10회의 실전 소개팅을 통해 완전히 달라졌습니다. 지금은 행복하게 만나고 있어요.",
  },
];

export default function LoveProjectPage() {
  const navigate = useNavigate();
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [applicationData, setApplicationData] = useState({
    name: "",
    gender: "",
    age: "",
    phone: "",
    email: "",
    region: "",
    occupation: "",
    motivation: "",
    experience: "",
    agreement: false,
  });
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setApplicationData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmitApplication = (e) => {
    e.preventDefault();
    setApplicationSubmitted(true);
    // 실제로는 백엔드 API 호출
  };

  const handleProceedToPayment = () => {
    setShowApplicationModal(false);
    setShowPaymentModal(true);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section - Full Image Background */}
      <section className="relative pt-24 pb-20 min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_9277ad9a-6109-40e4-9d7a-82032fc09839/artifacts/zb3pdlmh_ChatGPT%20Image%20Apr%206%2C%202026%2C%2004_14_56%20PM.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 20%',
          }}
        />
        {/* Light Overlay - only on left for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/70 via-white/30 to-transparent" style={{ width: '55%' }} />
        
        <div className="relative max-w-6xl mx-auto px-6 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-stone-900/80 rounded-full text-white text-sm font-medium mb-6">
              <Heart className="w-4 h-4" />
              14기 모집중
            </span>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-sans font-[720] mb-6" style={{color: '#FF69B4'}}>
              연인만들기 프로젝트
              <span className="block mt-2">14기 모집</span>
            </h1>
            
            <p className="text-lg md:text-xl text-stone-800 mb-8 leading-relaxed">
              단 3개월, 연애가 달라집니다
              <br />
              매력은 만들어지고, 인연은 연결됩니다
              <br />
              그 감동의 순간까지 리즈가 함께합니다.
            </p>

            <div className="flex flex-wrap gap-4 mb-10">
              <Button
                onClick={() => setShowApplicationModal(true)}
                className="px-8 py-6 bg-stone-900 hover:bg-stone-800 text-white font-bold text-lg rounded-full shadow-lg"
                data-testid="apply-btn"
              >
                지금 신청하기
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Quick Info Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { icon: Users, label: "대상", value: "40~60대" },
                { icon: Calendar, label: "기간", value: "7월 ~ 9월" },
                { icon: Star, label: "인원", value: "남녀 각 5명" },
                { icon: Heart, label: "목표", value: "연인 만들기" },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="bg-white/70 backdrop-blur-md rounded-xl p-3 border border-stone-200"
                >
                  <item.icon className="w-5 h-5 text-stone-700 mb-1" />
                  <p className="text-stone-500 text-xs">{item.label}</p>
                  <p className="text-stone-900 font-semibold text-sm">{item.value}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-stone-800 mb-6">
              이런 고민이 있으신가요?
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              "나는 연애를 해본 적이 없어 걱정이에요.",
              "어떻게 해야 매력적으로 보일지 모르겠어요.",
              "외모나 자신감이 부족해서 두렵습니다.",
            ].map((worry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-stone-100 rounded-2xl p-6 border-l-4 border-burgundy-500"
              >
                <p className="text-lg text-stone-700 italic">"{worry}"</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <p className="text-xl text-stone-600 leading-relaxed">
              이런 고민들을 가진 분들이,<br />
              <span className="text-burgundy-600 font-semibold">단 3개월 만에</span> 눈빛이 달라지고, 말투가 바뀌고,<br />
              자신감 넘치는 이성으로 변해갑니다.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Program Details */}
      <section className="py-20 bg-stone-100">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold-500 font-medium text-sm tracking-wider uppercase">Program</span>
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-stone-800 mt-2 mb-4">
              3개월 교육 과정
            </h2>
            <p className="text-stone-600">체계적인 커리큘럼으로 완전히 달라집니다</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {projectBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-14 h-14 bg-burgundy-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-burgundy-500" />
                </div>
                <h3 className="font-bold text-stone-800 mb-1">{benefit.title}</h3>
                <p className="text-sm text-stone-500">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Transformation Message */}
      <section className="py-20 bg-stone-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-2xl md:text-3xl text-white font-sans leading-relaxed mb-8">
              그리고 어느 순간 깨닫게 됩니다.
            </p>
            <p className="text-3xl md:text-4xl text-gold-400 font-bold italic">
              "아, 나도 충분히 사랑받을 만한<br />
              가치 있는 사람이구나."
            </p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-gold-500 font-medium text-sm tracking-wider uppercase">Success Stories</span>
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-stone-800 mt-2">
              수료생 후기
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-stone-50 rounded-2xl p-6 border border-stone-200"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-gold-500 fill-gold-500" />
                  ))}
                </div>
                <p className="text-stone-600 mb-4">"{testimonial.content}"</p>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-burgundy-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-burgundy-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-800">{testimonial.name}</p>
                    <p className="text-sm text-stone-500">{testimonial.age}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-stone-100">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl shadow-2xl overflow-hidden"
          >
            <div className="bg-gradient-to-r from-stone-500 to-stone-600 p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-2">14기 참가비</h3>
              <p className="text-4xl md:text-5xl font-bold text-gold-400">USD $2,500</p>
              <p className="text-white/70 mt-2">3개월 전체 프로그램</p>
            </div>

            <div className="p-8">
              <h4 className="font-bold text-stone-800 mb-4">프로그램 포함 내용</h4>
              <ul className="space-y-3 mb-8">
                {[
                  "이미지 트레이닝 (매력적인 이성되는 법)",
                  "멘탈 체인지 (가치높은 이성을 알아보는 안목)",
                  "1:1 개별 연애상담 및 코칭 (월 2회)",
                  "1:1 실시간 카톡상담",
                  "1:1 소개팅 10회 (실질적 경험쌓기)",
                  "전체 연애 코칭 (월 2회)",
                  "단톡방으로 정보 공유",
                  "외모 업그레이드 조언 (패션, 피부, 헤어, 분위기)",
                  "수료 후 6개월 힐링카페 무료가입",
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-stone-600">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-gold-50 rounded-xl p-4 mb-8">
                <p className="text-sm text-gold-800">
                  <strong>선착순 마감:</strong> 남성 5명, 여성 5명 (총 10명)
                </p>
              </div>

              <Button
                onClick={() => setShowApplicationModal(true)}
                className="w-full py-6 bg-gradient-to-r from-burgundy-500 to-burgundy-600 hover:from-burgundy-600 hover:to-burgundy-700 text-white font-bold text-lg rounded-xl"
                data-testid="apply-btn-bottom"
              >
                14기 신청하기
              </Button>

              <p className="text-center text-stone-500 text-sm mt-4">
                문의: info@newcoupling.com / newcoupling@gmail.com
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-stone-800 to-stone-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-white mb-6">
              여러분도 할 수 있습니다.<br />
              <span className="text-gold-400">지금이 바로 그때입니다.</span>
            </h2>
            <p className="text-white/70 mb-8">
              14기는 단순한 교육 과정이 아닙니다.<br />
              여러분의 인생을 바꿀 '사랑의 훈련소'이자, 새로운 인연을 만나는 시작점입니다.
            </p>
            <Button
              onClick={() => setShowApplicationModal(true)}
              className="px-10 py-6 bg-gold-500 hover:bg-gold-600 text-stone-900 font-bold text-lg rounded-full"
            >
              지금 바로 신청하기
            </Button>
          </motion.div>
        </div>
      </section>

      <FooterSection />

      {/* Application Modal */}
      <AnimatePresence>
        {showApplicationModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={() => setShowApplicationModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl my-8"
              onClick={(e) => e.stopPropagation()}
            >
              {!applicationSubmitted ? (
                <>
                  {/* Modal Header */}
                  <div className="bg-stone-100 border-b border-stone-200 p-6 text-stone-900 sticky top-0 z-10">
                    <button
                      onClick={() => setShowApplicationModal(false)}
                      className="absolute top-4 right-4 p-2 text-stone-500 hover:bg-stone-200 rounded-full transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                    <Heart className="w-10 h-10 mb-3 text-burgundy-500" />
                    <h3 className="text-2xl font-bold">14기 신청서</h3>
                    <p className="text-stone-500 text-sm">연인만들기 프로젝트</p>
                  </div>

                  {/* Application Form */}
                  <form onSubmit={handleSubmitApplication} className="p-6 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">
                          이름 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={applicationData.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                          placeholder="홍길동"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">
                          성별 <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 px-4 py-3 border border-stone-300 rounded-xl cursor-pointer hover:border-burgundy-300 flex-1">
                            <input
                              type="radio"
                              name="gender"
                              value="male"
                              required
                              checked={applicationData.gender === "male"}
                              onChange={handleInputChange}
                              className="text-burgundy-500"
                            />
                            <span>남성</span>
                          </label>
                          <label className="flex items-center gap-2 px-4 py-3 border border-stone-300 rounded-xl cursor-pointer hover:border-burgundy-300 flex-1">
                            <input
                              type="radio"
                              name="gender"
                              value="female"
                              checked={applicationData.gender === "female"}
                              onChange={handleInputChange}
                              className="text-burgundy-500"
                            />
                            <span>여성</span>
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">
                          나이 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="age"
                          required
                          min="40"
                          max="69"
                          value={applicationData.age}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                          placeholder="50"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">
                          연락처 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          value={applicationData.phone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                          placeholder="010-1234-5678"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">
                          이메일 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={applicationData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                          placeholder="example@email.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">
                          거주 지역 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="region"
                          required
                          value={applicationData.region}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                          placeholder="서울, 토론토 등"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        직업
                      </label>
                      <input
                        type="text"
                        name="occupation"
                        value={applicationData.occupation}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                        placeholder="직업을 입력해주세요"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        신청 동기 <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="motivation"
                        required
                        rows={3}
                        value={applicationData.motivation}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500 resize-none"
                        placeholder="연인만들기 프로젝트에 신청하게 된 동기를 적어주세요"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        연애 경험
                      </label>
                      <textarea
                        name="experience"
                        rows={2}
                        value={applicationData.experience}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500 resize-none"
                        placeholder="현재 연애 상황이나 과거 경험을 간단히 적어주세요 (선택)"
                      />
                    </div>

                    <div className="bg-stone-50 rounded-xl p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="agreement"
                          required
                          checked={applicationData.agreement}
                          onChange={handleInputChange}
                          className="mt-1 text-burgundy-500"
                        />
                        <span className="text-sm text-stone-600">
                          개인정보 수집 및 이용에 동의합니다. 수집된 정보는 프로젝트 진행 목적으로만 사용됩니다.
                          <span className="text-red-500"> *</span>
                        </span>
                      </label>
                    </div>

                    <Button
                      type="submit"
                      className="w-full py-6 bg-gradient-to-r from-burgundy-500 to-burgundy-600 hover:from-burgundy-600 hover:to-burgundy-700 text-white font-bold text-lg rounded-xl"
                    >
                      신청서 제출하기
                    </Button>
                  </form>
                </>
              ) : (
                /* Success & Payment */
                <div className="p-8 text-center">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="w-10 h-10 text-emerald-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-stone-800 mb-2">신청서 제출 완료!</h3>
                  <p className="text-stone-600 mb-8">
                    신청해 주셔서 감사합니다.<br />
                    참가비 결제를 진행해 주세요.
                  </p>

                  <div className="bg-stone-100 rounded-xl p-6 mb-6">
                    <p className="text-sm text-stone-500 mb-2">결제 금액</p>
                    <p className="text-3xl font-bold text-stone-800">USD $2,500</p>
                  </div>

                  <Button
                    onClick={handleProceedToPayment}
                    className="w-full py-6 bg-gradient-to-r from-burgundy-500 to-burgundy-600 hover:from-burgundy-600 hover:to-burgundy-700 text-white font-bold text-lg rounded-xl mb-4"
                  >
                    <CreditCard className="w-5 h-5 mr-2" />
                    결제하기
                  </Button>

                  <button
                    onClick={() => setShowApplicationModal(false)}
                    className="text-stone-500 hover:text-stone-700 text-sm"
                  >
                    나중에 결제하기
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowPaymentModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-stone-800 to-stone-900 p-6 text-white">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <CreditCard className="w-10 h-10 mb-3" />
                <h3 className="text-2xl font-bold">결제하기</h3>
                <p className="text-white/80 text-sm">연인만들기 프로젝트 14기</p>
              </div>

              <div className="p-6">
                <div className="bg-stone-100 rounded-xl p-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-stone-600">참가비</span>
                    <span className="text-xl font-bold text-stone-800">USD $2,500</span>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">카드 번호</label>
                    <input
                      type="text"
                      placeholder="1234 5678 9012 3456"
                      className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">유효기간</label>
                      <input
                        type="text"
                        placeholder="MM/YY"
                        className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">CVC</label>
                      <input
                        type="text"
                        placeholder="123"
                        className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => {
                    alert("결제가 완료되었습니다! 담당자가 곧 연락드리겠습니다.");
                    setShowPaymentModal(false);
                  }}
                  className="w-full py-6 bg-gradient-to-r from-burgundy-500 to-burgundy-600 hover:from-burgundy-600 hover:to-burgundy-700 text-white font-bold text-lg rounded-xl"
                >
                  USD $2,500 결제하기
                </Button>

                <p className="text-center text-stone-400 text-xs mt-4">
                  안전한 결제를 위해 SSL 암호화가 적용됩니다
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
